// Central analytics event tracker.
// All calls are safe: they no-op gracefully if tracking scripts haven't loaded.
import { getStoredUTM, clearUTM } from './utm';

// Only log when user explicitly opts in — works on real devices + previews.
function _debugEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('analytics_debug') === '1') return true;
    if (window.location.search.includes('debug=1')) {
      localStorage.setItem('analytics_debug', '1');
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function _log(...args) {
  if (_debugEnabled()) {
    // eslint-disable-next-line no-console
    console.log('[Analytics]', ...args);
  }
}

// Map custom app events → Meta standard events where applicable.
const META_STANDARD = {
  Purchase: 'Purchase',
  Lead_Captured: 'Lead',
  Initiate_Checkout: 'InitiateCheckout',
};

// Generate a single event_id (UUID) to deduplicate a browser Pixel event with
// its server-side CAPI twin. Caller generates ONCE and passes the SAME id to
// both the Pixel (via trackEvent options.eventID) and the backend request body.
export function newEventId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}

// options:
//   eventID      — shared Meta dedup key (also passed to the backend CAPI call)
//   metaStandard — override the Meta standard event name (else META_STANDARD map)
export function trackEvent(eventName, properties = {}, options = {}) {
  const enriched = { ...properties, ...getStoredUTM() };
  const { eventID, metaStandard } = options;

  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, enriched);
    }
  } catch (e) {
    _log('gtag error', e);
  }

  try {
    if (typeof window !== 'undefined' && window.fbq) {
      const fbOpts = eventID ? { eventID } : undefined;
      if (fbOpts) window.fbq('trackCustom', eventName, enriched, fbOpts);
      else window.fbq('trackCustom', eventName, enriched);
      const standard = metaStandard || META_STANDARD[eventName];
      if (standard) {
        if (fbOpts) window.fbq('track', standard, enriched, fbOpts);
        else window.fbq('track', standard, enriched);
      }
    }
  } catch (e) {
    _log('fbq error', e);
  }

  _log(eventName, enriched, eventID ? `[eventID=${eventID}]` : '');
}

export function trackPageView(path) {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', { page_path: path });
    }
  } catch {
    // ignore
  }
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView');
    }
  } catch {
    // ignore
  }
  _log('page_view', path);
}

export function trackPurchase(transactionId, value, planName) {
  const payload = {
    transaction_id: transactionId,
    value,
    currency: 'INR',
    items: [{ item_name: planName }],
  };
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'purchase', payload);
    }
  } catch {
    // ignore
  }
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Purchase', {
        value,
        currency: 'INR',
        content_name: planName,
      });
    }
  } catch {
    // ignore
  }
  _log('Purchase', payload);
  // Reset attribution after a successful purchase
  clearUTM();
}
